import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../services/api/client.js';
import { formatAppDate } from '../utils/locale.js';
import { friendlyErrorMessage } from '../lib/notifications.js';
import { createPollingClock } from '../utils/pollingClock.js';
import { batchWithJobs, generationFailureForItem } from '../lib/generationFailure.js';
import { aggregateMetrics, normalizeMetrics, refreshOutcome } from '../lib/socialMetrics.js';

const ContentFeedContext = createContext(null);
const ACTIVE_BATCH_STATUSES = new Set(['queued', 'generating', 'reserved']);
const CONTENT_PAGE_LIMIT = 12;
// A freshly-created batch may not appear in the content feed for a beat (backend
// ingest lag). Keep an unconfirmed-but-just-created batch alive this long before
// we trust the feed's "not active" verdict and evict it.
const STALE_ACTIVE_BATCH_GRACE_MS = 60 * 1000;
// Absolute backstop: no real generation runs this long (single-flight + queue
// concurrency). A batch still flagged "active" past this age is treated as
// settled/stuck and dropped, so it can never hang the New Post / assistant lock.
const MAX_ACTIVE_BATCH_AGE_MS = 30 * 60 * 1000;
const SETTLE_POLL_INTERVAL_MS = 4000;
const SETTLE_POLL_TICKS = 10;
const PUBLISH_POLL_INTERVAL_MS = 4000;
const PUBLISH_POLL_TICKS = 30;
const PUBLISH_POLL_LOOKAHEAD_MS = 60 * 1000;
const ACTIVE_METRICS_USER_BUDGET = 3;
const ACTIVE_METRICS_USER_WINDOW_MS = 60 * 1000;
const ACTIVE_METRICS_CONTENT_COOLDOWN_MS = 60 * 1000;
const ACTIVE_METRICS_REFRESH_DELAY_MS = 2500;
const ACTIVE_VIEW_METRIC_PLATFORMS = new Set(['instagram', 'facebook', 'threads', 'telegram']);
const PENDING_METRICS_REPAINT_FAST_MS = 15 * 1000;
const PENDING_METRICS_REPAINT_SLOW_MS = 60 * 1000;
const PENDING_METRICS_REPAINT_MAX_MS = 10 * 60 * 1000;

export function ContentFeedProvider({ children }) {
  const [content, setContent] = useState([]);
  const [activeBatches, setActiveBatches] = useState([]);
  // Latest active batches, for interval closures that must read fresh state
  // without re-arming their timer on every change.
  const activeBatchesRef = useRef([]);
  const [loading, setLoading] = useState(true);
  // A background refresh clears `error` without setting `loading`. Neither
  // field therefore proves the initial empty counts came from the server.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [metricRefreshFeedback, setMetricRefreshFeedback] = useState(null);
  const aliveRef = useRef(true);
  const feedScopeRef = useRef(api.getReadScope());
  const feedRequestRef = useRef(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [counts, setCounts] = useState(() => emptyContentCounts());
  const [statusFilter, setStatusFilter] = useState('all');
  const contentRef = useRef([]);
  const statusFilterRef = useRef('all');
  const settlePollRef = useRef({ stop: null, remaining: 0 });
  const publishPollRef = useRef({ stop: null, remaining: 0, activeSignature: '' });
  const activeMetricsRef = useRef({ windowStartedAt: 0, count: 0, byContentId: new Map(), refreshTimeoutId: null });
  const pendingMetricsRef = useRef({ timeoutId: null, signature: '', startedAt: 0, tick: 0 });

  async function refreshContent({ background = false, refreshMetrics = true, status = statusFilterRef.current } = {}) {
    const scope = api.getReadScope();
    const request = ++feedRequestRef.current;
    setLoadingMore(false);
    const current = () => aliveRef.current && scope === api.getReadScope() && request === feedRequestRef.current;
    const nextStatusFilter = normalizeFeedStatus(status);
    const statusChanged = nextStatusFilter !== statusFilterRef.current;
    if (statusChanged) {
      statusFilterRef.current = nextStatusFilter;
      setStatusFilter(nextStatusFilter);
      contentRef.current = [];
      setContent([]);
      setNextCursor(null);
      setHasLoaded(false);
    }
    try {
      if (!background || statusChanged) setLoading(true);
      setError(null);
      const feed = await fetchContentFeedRows({ limit: CONTENT_PAGE_LIMIT, status: nextStatusFilter });
      if (!current()) return null;
      const rows = feed.rows;
      const activeBatchIds = activeBatchIdsForRows(rows);
      const displayBatchIds = displayBatchIdsForRows(rows);
      const feedBatchById = new Map(feed.batches.map(batch => [batch.id, batch]));
      const missingBatchIds = batchDetailsNeeded(displayBatchIds, feedBatchById, rows);
      const missingBatches = await fetchBatchesForIds(missingBatchIds);
      if (!current()) return null;
      const batches = [...feed.batches, ...missingBatches];
      const batchById = new Map(batches.map(batch => [batch.id, batch]));
      const fetchedContent = rows.map(row => {
        const item = row.contentItem || row;
        return mapContentRow(row, row.publishSummary || null, batchById.get(item.generationBatchId) || null);
      });
      const nextContent = background && !statusChanged ? mergeContentForBackground(contentRef.current, fetchedContent) : fetchedContent;
      // Keep every batch that's still in an ACTIVE status from `previous` —
      // don't drop a freshly-created batch just because its items haven't
      // been ingested into this feed snapshot yet (pagination / race /
      // text-saved-but-visual-cooking transitions can all make the feed
      // temporarily exclude items even though the batch is still alive).
      // The merge with `batches` below will reconcile counts/status; the
      // sortActiveBatches step inside mergeFetchedBatches drops anything
      // that has actually transitioned to a terminal status.
      setActiveBatches(previous => mergeFetchedBatches(previous, batches));
      contentRef.current = nextContent;
      setContent(nextContent);
      setNextCursor(feed.nextCursor || null);
      setCounts(normalizeContentCounts(feed.counts, nextContent));
      setHasLoaded(true);
      updatePublishPolling(nextContent);
      updatePendingMetricsRepaint(nextContent);
      if (refreshMetrics) requestActiveMetricsRefresh(nextContent);
      return { content: nextContent, batches, activeBatchIds };
    } catch (err) {
      if (!current()) return null;
      setError(friendlyErrorMessage(err, 'Could not load the Library. Refresh in a moment.'));
      return null;
    } finally {
      // A newer background read can supersede the initial foreground request.
      // Whichever current read settles must release that initial loading state.
      if (current()) setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor || loadingMore) return null;
    const scope = api.getReadScope();
    const request = feedRequestRef.current;
    const current = () => aliveRef.current && scope === api.getReadScope() && request === feedRequestRef.current;
    setLoadingMore(true);
    setError(null);
    try {
      const feed = await fetchContentFeedRows({ limit: CONTENT_PAGE_LIMIT, cursor: nextCursor, status: statusFilterRef.current });
      if (!current()) return null;
      const feedBatchById = new Map(feed.batches.map(batch => [batch.id, batch]));
      const displayBatchIds = displayBatchIdsForRows(feed.rows);
      const missingBatchIds = batchDetailsNeeded(displayBatchIds, feedBatchById, feed.rows);
      const missingBatches = await fetchBatchesForIds(missingBatchIds);
      if (!current()) return null;
      const batchById = new Map([...feed.batches, ...missingBatches].map(batch => [batch.id, batch]));
      const fetchedContent = feed.rows.map(row => {
        const item = row.contentItem || row;
        return mapContentRow(row, row.publishSummary || null, batchById.get(item.generationBatchId) || null);
      });
      const existingIds = new Set(contentRef.current.map(item => item.id));
      const dedup = fetchedContent.filter(item => !existingIds.has(item.id));
      const merged = [...contentRef.current, ...dedup];
      contentRef.current = merged;
      setContent(merged);
      setNextCursor(feed.nextCursor || null);
      if (feed.counts) setCounts(normalizeContentCounts(feed.counts, merged));
      return { added: dedup.length };
    } catch (err) {
      if (!current()) return null;
      setError(friendlyErrorMessage(err, 'Could not load more posts. Try again.'));
      return null;
    } finally {
      if (current()) setLoadingMore(false);
    }
  }

  async function hydrateContentItem(id) {
    if (!id) return null;
    const scope = api.getReadScope();
    const full = await api.content.get(id);
    if (!aliveRef.current || scope !== api.getReadScope()) return null;
    const previous = contentRef.current.find(item => item.id === id) || null;
    const previousRaw = previous?.raw || null;
    const mergedRaw = mergeRawContentForMedia(previousRaw, full);
    const mapped = mapContentRow(
      mergedRaw,
      mergedRaw.publishSummary || previous?.publishSummary || null,
      previous?.batch || null
    );
    const nextItem = mergeContentItem(previous, mapped);
    const exists = contentRef.current.some(item => item.id === id);
    const nextContent = exists
      ? contentRef.current.map(item => item.id === id ? nextItem : item)
      : [nextItem, ...contentRef.current];
    contentRef.current = nextContent;
    setContent(nextContent);
    return nextItem.raw;
  }

  function upsertContentItems(items, batch = null) {
    if (!Array.isArray(items) || items.length === 0) return;
    const mapped = items.map(row => mapContentRow(row, row?.publishSummary || null, batch));
    const nextContent = mergeUpsertedContent(contentRef.current, mapped);
    contentRef.current = nextContent;
    setContent(nextContent);
  }

  function startGenerationBatch(batch, items) {
    if (batch) {
      setActiveBatches(previous => upsertBatch(previous, batch));
    }
    upsertContentItems(items, batch);
  }

  async function pollActiveBatches() {
    const scope = api.getReadScope();
    const active = activeBatches.filter(batch => ACTIVE_BATCH_STATUSES.has(batch.status));
    if (!active.length) return;
    const previousActiveIds = new Set(active.map(batch => batch.id));
    const result = await refreshContent({ background: true });
    if (!result || scope !== api.getReadScope()) return;
    // Evict batches that have actually settled. The content feed only carries
    // batches that still have generating/failed items, so a batch that finished
    // cleanly drops OUT of it — mergeFetchedBatches would otherwise keep our stale
    // 'generating' copy forever, hanging the single-flight lock that gates New
    // Post AND the assistant. Keep a batch only if the fresh fetch still confirms
    // it active, or it was created moments ago (feed ingest lag for a just-started
    // batch). This is what made the status hang until a full page reload.
    const now = Date.now();
    setActiveBatches(previous => previous.filter(batch => {
      if (!ACTIVE_BATCH_STATUSES.has(batch.status)) return false;
      if (result.activeBatchIds.has(batch.id)) return true;
      const fresh = result.batches.find(item => item.id === batch.id);
      if (fresh && ACTIVE_BATCH_STATUSES.has(fresh.status)) return true;
      const created = Date.parse(batch.createdAt || batch.updatedAt || '');
      return !Number.isNaN(created) && (now - created) < STALE_ACTIVE_BATCH_GRACE_MS;
    }));
    const settled = [...previousActiveIds].some(id => {
      const batch = result.batches.find(item => item.id === id);
      return !result.activeBatchIds.has(id) || (batch && !ACTIVE_BATCH_STATUSES.has(batch.status));
    });
    if (settled) {
      startSettlePolling();
    }
  }

  function startSettlePolling() {
    settlePollRef.current.remaining = Math.max(settlePollRef.current.remaining, SETTLE_POLL_TICKS);
    if (settlePollRef.current.stop) return;
    settlePollRef.current.stop = createPollingClock(async () => {
      if (settlePollRef.current.remaining <= 0) {
        settlePollRef.current.stop?.();
        settlePollRef.current.stop = null;
        return;
      }
      settlePollRef.current.remaining -= 1;
      await refreshContent({ background: true });
    }, { minIntervalMs: SETTLE_POLL_INTERVAL_MS, maxIntervalMs: 30_000, fireImmediately: false });
  }

  function updatePublishPolling(items) {
    const signature = duePublishingSignature(items);
    if (!signature) {
      publishPollRef.current.activeSignature = '';
      publishPollRef.current.remaining = 0;
      publishPollRef.current.stop?.();
      publishPollRef.current.stop = null;
      return;
    }

    if (publishPollRef.current.activeSignature !== signature) {
      publishPollRef.current.activeSignature = signature;
      publishPollRef.current.remaining = PUBLISH_POLL_TICKS;
    }
    if (!publishPollRef.current.stop && publishPollRef.current.remaining > 0) {
      publishPollRef.current.stop = createPollingClock(async () => {
        if (publishPollRef.current.remaining <= 0) {
          publishPollRef.current.stop?.();
          publishPollRef.current.stop = null;
          return;
        }
        publishPollRef.current.remaining -= 1;
        await refreshContent({ background: true });
      }, { minIntervalMs: PUBLISH_POLL_INTERVAL_MS, maxIntervalMs: 30_000, fireImmediately: false });
    }
  }

  function requestActiveMetricsRefresh(items) {
    if (typeof window === 'undefined') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (!isContentRouteActive()) return;
    const candidates = items.filter(shouldRefreshActiveMetrics).slice(0, ACTIVE_METRICS_USER_BUDGET);
    for (const item of candidates) {
      if (!consumeActiveMetricsBudget(item.id)) continue;
      const scope = api.getReadScope();
      api.content.refreshMetrics(item.id, { limit: 5 })
        .then(result => {
          if (!aliveRef.current || scope !== api.getReadScope()) return;
          const outcome = refreshOutcome(result);
          if (outcome.refreshed) scheduleMetricsRefreshReload();
          if (!outcome.complete) setMetricRefreshFeedback(outcome.refreshed ? 'refreshPartial' : 'refreshUnavailable');
        })
        .catch(error => {
          if (!aliveRef.current || scope !== api.getReadScope()) return;
          if (refreshOutcome(error).refreshed) scheduleMetricsRefreshReload();
          setMetricRefreshFeedback(refreshOutcome(error).refreshed ? 'refreshPartial' : 'refreshUnavailable');
        });
    }
  }

  function isContentRouteActive() {
    // Routes that show Library cards or per-post metric tiles. Without this
    // gate the metrics-refresh + passive-repaint loops never fire, and IG /
    // Threads insights stay stale until the 30-min engagement-worker cron
    // pulls them in. Includes legacy /content/* aliases for old bookmarks.
    const path = window.location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
    if (path === '/' || path === '') return true;
    if (path === '/library' || path.startsWith('/library/')) return true;
    if (path === '/post' || path.startsWith('/post/')) return true;
    if (path === '/calendar' || path.startsWith('/calendar/')) return true;
    if (path === '/batch' || path.startsWith('/batch/')) return true;
    if (path === '/content' || path.startsWith('/content/')) return true;
    return false;
  }

  function consumeActiveMetricsBudget(contentId) {
    const now = Date.now();
    const state = activeMetricsRef.current;
    if (now - state.windowStartedAt >= ACTIVE_METRICS_USER_WINDOW_MS) {
      state.windowStartedAt = now;
      state.count = 0;
    }
    if (state.count >= ACTIVE_METRICS_USER_BUDGET) return false;
    const previous = state.byContentId.get(contentId) || 0;
    if (now - previous < ACTIVE_METRICS_CONTENT_COOLDOWN_MS) return false;
    state.count += 1;
    state.byContentId.set(contentId, now);
    return true;
  }

  function scheduleMetricsRefreshReload() {
    if (activeMetricsRef.current.refreshTimeoutId) return;
    const scope = api.getReadScope();
    activeMetricsRef.current.refreshTimeoutId = window.setTimeout(async () => {
      activeMetricsRef.current.refreshTimeoutId = null;
      if (!aliveRef.current || scope !== api.getReadScope()) return;
      await refreshContent({ background: true, refreshMetrics: false });
    }, ACTIVE_METRICS_REFRESH_DELAY_MS);
  }

  function updatePendingMetricsRepaint(items) {
    if (!canPassiveMetricsRepaint()) {
      stopPendingMetricsRepaint();
      return;
    }
    const signature = pendingViewMetricSignature(items);
    if (!signature) {
      stopPendingMetricsRepaint();
      return;
    }

    const state = pendingMetricsRef.current;
    const now = Date.now();
    if (state.signature !== signature) {
      if (state.timeoutId) window.clearTimeout(state.timeoutId);
      state.timeoutId = null;
      state.signature = signature;
      state.startedAt = now;
      state.tick = 0;
    }
    if (now - state.startedAt >= PENDING_METRICS_REPAINT_MAX_MS) {
      stopPendingMetricsRepaint();
      return;
    }
    if (!state.timeoutId) schedulePendingMetricsRepaintTick();
  }

  function schedulePendingMetricsRepaintTick() {
    const state = pendingMetricsRef.current;
    const delay = state.tick === 0 ? PENDING_METRICS_REPAINT_FAST_MS : PENDING_METRICS_REPAINT_SLOW_MS;
    state.timeoutId = window.setTimeout(async () => {
      state.timeoutId = null;
      if (!canPassiveMetricsRepaint()) {
        stopPendingMetricsRepaint();
        return;
      }
      state.tick += 1;
      await refreshContent({ background: true, refreshMetrics: false });
    }, delay);
  }

  function stopPendingMetricsRepaint() {
    const state = pendingMetricsRef.current;
    if (state.timeoutId) window.clearTimeout(state.timeoutId);
    state.timeoutId = null;
    state.signature = '';
    state.startedAt = 0;
    state.tick = 0;
  }

  function canPassiveMetricsRepaint() {
    if (typeof window === 'undefined') return false;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
    return isContentRouteActive();
  }

  useEffect(() => {
    aliveRef.current = true;
    const stopScopePolling = () => {
      settlePollRef.current.stop?.();
      settlePollRef.current = { stop: null, remaining: 0 };
      publishPollRef.current.stop?.();
      publishPollRef.current = { stop: null, remaining: 0, activeSignature: '' };
      if (activeMetricsRef.current.refreshTimeoutId) window.clearTimeout(activeMetricsRef.current.refreshTimeoutId);
      activeMetricsRef.current.refreshTimeoutId = null;
      stopPendingMetricsRepaint();
    };
    const unsubscribe = api.subscribeReadScope(({ scope, reset, reload }) => {
      if (!aliveRef.current || scope === feedScopeRef.current) return;
      feedScopeRef.current = scope;
      feedRequestRef.current += 1;
      stopScopePolling();
      if (reset) {
        // Only feed-owned snapshots and activity belong to the old identity.
        // Keep the user's status filter and the separate billing state intact.
        contentRef.current = [];
        activeBatchesRef.current = [];
        setContent([]);
        setActiveBatches([]);
        setCounts(emptyContentCounts());
        setNextCursor(null);
        setHasLoaded(false);
        setLoadingMore(false);
        setError(null);
        setMetricRefreshFeedback(null);
        setLoading(reload);
      }
      // Cache-only invalidations (e.g. billing) retain readable observations.
      // Logout invalidates outstanding reads without issuing an anonymous retry.
      if (reload) refreshContent({ background: !reset });
    });
    feedScopeRef.current = api.getReadScope();
    refreshContent();
    const handleVisibleRefresh = () => {
      if (canPassiveMetricsRepaint() && pendingViewMetricSignature(contentRef.current)) {
        refreshContent({ background: true, refreshMetrics: false });
      }
    };
    window.addEventListener('focus', handleVisibleRefresh);
    document.addEventListener('visibilitychange', handleVisibleRefresh);
    return () => {
      aliveRef.current = false;
      unsubscribe();
      window.removeEventListener('focus', handleVisibleRefresh);
      document.removeEventListener('visibilitychange', handleVisibleRefresh);
      stopScopePolling();
    };
  }, []);

  useEffect(() => {
    if (!activeBatches.some(batch => ACTIVE_BATCH_STATUSES.has(batch.status))) return undefined;
    return createPollingClock(() => pollActiveBatches(), {
      minIntervalMs: 4000,
      maxIntervalMs: 60_000,
      fireImmediately: false,
    });
  }, [activeBatches]);

  // Keep the ref fresh for the discovery interval below.
  useEffect(() => { activeBatchesRef.current = activeBatches; }, [activeBatches]);

  // Discovery poll: surface SERVER-INITIATED batches (the autopilot's weekly
  // generation) that the user didn't start in this tab. The fast poll above only
  // CONTINUES tracking already-known active batches, so a background batch never
  // entered activeBatches — the topbar Activity pill stayed empty while autopilot
  // generated, and the only way in was the notification. Slow, visible-only, and
  // skipped while a batch is already active (the fast poll owns that case), so
  // the cost is one idle background feed refresh per minute while the app is open.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (activeBatchesRef.current.some(batch => ACTIVE_BATCH_STATUSES.has(batch.status))) return;
      refreshContent({ background: true, refreshMetrics: false });
    }, 60000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    metricRefreshFeedback,
    content,
    activeBatches,
    loading,
    hasLoaded,
    loadingMore,
    error,
    counts,
    statusFilter,
    nextCursor,
    hasMore: Boolean(nextCursor),
    refreshContent,
    loadMore,
    hydrateContentItem,
    startGenerationBatch,
    upsertContentItems,
    pollActiveBatches,
  };

  return <ContentFeedContext.Provider value={value}>{children}</ContentFeedContext.Provider>;
}

async function fetchContentFeedRows({ limit = CONTENT_PAGE_LIMIT, cursor = null, status = 'all' } = {}) {
  try {
    const filters = { limit, view: 'list' };
    const normalizedStatus = normalizeFeedStatus(status);
    if (normalizedStatus !== 'all') filters.status = normalizedStatus;
    if (cursor) filters.cursor = cursor;
    const response = await api.content.feed(filters);
    return {
      rows: response.content || [],
      batches: response.batches || [],
      counts: response.counts || null,
      nextCursor: response.nextCursor || null
    };
  } catch (err) {
    if (err?.status !== 404) throw err;
  }

  const listFilters = { limit };
  const normalizedStatus = normalizeFeedStatus(status);
  if (normalizedStatus !== 'all') listFilters.status = normalizedStatus;
  if (cursor) listFilters.cursor = cursor;
  const [response, scheduleResponse, publishedScheduleResponse] = await Promise.all([
    api.content.list(listFilters),
    api.scheduler.list({ limit: 100 }),
    api.scheduler.list({ limit: 100, status: 'published' }),
  ]);
  const scheduleByContent = buildScheduleByContent(mergeSchedulePosts(scheduleResponse.posts || [], publishedScheduleResponse.posts || []));
  const rows = (response.content || []).map(row => {
    const item = row.contentItem || row;
    return { ...row, publishSummary: scheduleByContent.get(item.id) || null };
  });
  return { rows, batches: [], counts: null, nextCursor: response.nextCursor || null };
}

export function useContentFeedContext() {
  const context = useContext(ContentFeedContext);
  if (!context) {
    throw new Error('useContentFeed must be used inside ContentFeedProvider');
  }
  return context;
}

function mergeContentForBackground(previous, incoming) {
  if (!previous.length) return incoming;
  const previousById = new Map(previous.map(item => [item.id, item]));
  const incomingIds = new Set(incoming.map(item => item.id));
  const refreshed = incoming.map(item => mergeContentItem(previousById.get(item.id), item));
  const previousOnly = previous.filter(item => !incomingIds.has(item.id));
  return [...refreshed, ...previousOnly];
}

function mergeUpsertedContent(previous, incoming) {
  const previousById = new Map(previous.map(item => [item.id, item]));
  const incomingById = new Map(incoming.map(item => [item.id, item]));
  const incomingIds = new Set(incoming.map(item => item.id));
  const newItems = incoming.filter(item => !previousById.has(item.id));
  const existing = previous.map(item => incomingIds.has(item.id) ? mergeContentItem(item, incomingById.get(item.id)) : item);
  return [...newItems, ...existing];
}

function mergeContentItem(previous, incoming) {
  if (!previous) return incoming;
  if (!incoming) return previous;
  const merged = { ...previous, ...incoming };
  const previousMediaScore = loadedMediaScore(previous);
  const incomingMediaScore = loadedMediaScore(incoming);
  if (previousMediaScore > incomingMediaScore) {
    merged.imageUrl = previous.imageUrl || incoming.imageUrl;
    merged.imageUrls = previous.imageUrls?.length ? previous.imageUrls : incoming.imageUrls;
    merged.mediaPreview = mergeMediaPreview(incoming.mediaPreview, previous.mediaPreview);
    merged.raw = mergeRawContentForMedia(incoming.raw, previous.raw);
  }
  return merged;
}

function emptyContentCounts() {
  return {
    all: 0,
    generating: 0,
    ready: 0,
    planned: 0,
    scheduled: 0,
    publishing: 0,
    published: 0,
    draft: 0,
    failed: 0,
  };
}

function normalizeFeedStatus(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : 'all';
}

function normalizeContentCounts(counts, fallbackItems = []) {
  const next = emptyContentCounts();
  if (counts && typeof counts === 'object') {
    Object.entries(counts).forEach(([key, value]) => {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric >= 0) next[key] = numeric;
    });
    return next;
  }

  fallbackItems.forEach(item => {
    const status = item?.status || 'draft';
    next[status] = (next[status] || 0) + 1;
    next.all += 1;
  });
  return next;
}

function mergeRawContentForMedia(base, mediaSource) {
  if (!base) return mediaSource;
  if (!mediaSource) return base;
  const baseSlides = Array.isArray(base.carouselSlides) ? base.carouselSlides : [];
  const sourceSlides = Array.isArray(mediaSource.carouselSlides) ? mediaSource.carouselSlides : [];
  const baseUrls = Array.isArray(base.mediaUrls) ? base.mediaUrls : [];
  const sourceUrls = Array.isArray(mediaSource.mediaUrls) ? mediaSource.mediaUrls : [];
  const useSourceMedia = sourceSlides.length + sourceUrls.length > baseSlides.length + baseUrls.length;
  const media = useSourceMedia ? mediaSource : base;
  return {
    ...base,
    ...mediaSource,
    contentItem: { ...(base.contentItem || {}), ...(mediaSource.contentItem || {}) },
    activeVersion: {
      ...(base.activeVersion || {}),
      ...(mediaSource.activeVersion || {}),
      mediaUrls: media.activeVersion?.mediaUrls || media.mediaUrls || [],
      mediaPreview: mergeMediaPreview(mediaSource.activeVersion?.mediaPreview, media.activeVersion?.mediaPreview || media.mediaPreview),
    },
    carouselSlides: media.carouselSlides || [],
    mediaUrls: media.mediaUrls || media.activeVersion?.mediaUrls || [],
    mediaPreview: mergeMediaPreview(base.mediaPreview, mediaSource.mediaPreview),
    publishSummary: base.publishSummary || mediaSource.publishSummary || null,
    generationBatch: base.generationBatch || mediaSource.generationBatch || null,
    publishPlatforms: base.publishPlatforms || mediaSource.publishPlatforms,
    publishTargetAccountIds: base.publishTargetAccountIds || mediaSource.publishTargetAccountIds,
    publishMode: base.publishMode || mediaSource.publishMode,
    publishScheduleAt: base.publishScheduleAt || mediaSource.publishScheduleAt,
  };
}

function loadedMediaScore(item) {
  const raw = item?.raw || item || {};
  return Math.max(
    Array.isArray(item?.imageUrls) ? item.imageUrls.length : 0,
    Array.isArray(raw.carouselSlides) ? raw.carouselSlides.length : 0,
    Array.isArray(raw.mediaUrls) ? raw.mediaUrls.length : 0,
    Array.isArray(raw.activeVersion?.mediaUrls) ? raw.activeVersion.mediaUrls.length : 0
  );
}

function mergeMediaPreview(base, next) {
  return {
    ...(base || {}),
    ...(next || {}),
    count: Math.max(Number(base?.count || 0), Number(next?.count || 0)),
    hasCarousel: Boolean(base?.hasCarousel || next?.hasCarousel || Number(base?.count || 0) > 1 || Number(next?.count || 0) > 1),
  };
}

function upsertBatch(previous, batch) {
  const byId = new Map(previous.map(item => [item.id, item]));
  byId.set(batch.id, { ...byId.get(batch.id), ...batch });
  return sortActiveBatches(Array.from(byId.values()));
}

function mergeFetchedBatches(previous, batches) {
  const byId = new Map(previous.map(item => [item.id, item]));
  batches.forEach(batch => byId.set(batch.id, { ...byId.get(batch.id), ...batch }));
  return sortActiveBatches(Array.from(byId.values()));
}

function sortActiveBatches(batches) {
  const now = Date.now();
  return batches
    .filter(batch => ACTIVE_BATCH_STATUSES.has(batch.status))
    .filter(batch => {
      // Absolute backstop against a batch whose status never flips terminal
      // server-side: anything "active" far longer than any real generation takes
      // is dropped so it can't hang the lock indefinitely. createdAt missing →
      // keep (a just-created batch may not carry a timestamp yet).
      const created = Date.parse(batch.createdAt || batch.updatedAt || '');
      return Number.isNaN(created) || (now - created) < MAX_ACTIVE_BATCH_AGE_MS;
    })
    .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0));
}

function mergeSchedulePosts(...groups) {
  const byId = new Map();
  groups.flat().forEach(row => {
    const post = row?.post || row;
    if (!post?.id) return;
    byId.set(post.id, { ...byId.get(post.id), ...row });
  });
  return Array.from(byId.values());
}

function timestamp(value) {
  const parsed = Date.parse(value || '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

function duePublishingSignature(items) {
  const now = Date.now();
  return items
    .filter(item => ['scheduled', 'publishing'].includes(item.status))
    .filter(item => item.publishSummary?.activeCount > 0)
    .filter(item => item.status === 'publishing' || timestamp(item.scheduledAt) <= now + PUBLISH_POLL_LOOKAHEAD_MS)
    .map(item => item.id)
    .sort()
    .join('|');
}

function shouldRefreshActiveMetrics(item) {
  if (item.status !== 'published') return false;
  const targets = Array.isArray(item.publishSummary?.targets) ? item.publishSummary.targets : [];
  const publishedTargets = targets.filter(target => target.status === 'published');
  if (!publishedTargets.length) return false;
  return publishedTargets.some(shouldRefreshTargetMetrics);
}

function shouldRefreshTargetMetrics(target) {
  if (['x', 'twitter'].includes(String(target.platform).toLowerCase())) return false;
  if (timestamp(target.metricFetch?.nextFetchAt) > Date.now()) return false;
  if (['authentication_failed', 'permission_denied', 'scope_required', 'read_budget_required', 'unsupported_platform', 'missing_account_or_token'].includes(target.metricFetch?.reason)) return false;
  const publishedAt = timestamp(target.publishedAt);
  if (!publishedAt) return false;
  const metrics = target.metrics || target.analytics || target.insights || target.stats || null;
  const fetchedAt = timestamp(metrics?.fetchedAt);
  if (!fetchedAt) return true;
  return Date.now() - fetchedAt >= activeMetricStaleMs(publishedAt);
}

function pendingViewMetricSignature(items) {
  const now = Date.now();
  return items
    .filter(item => item.status === 'published')
    .flatMap(item => pendingViewMetricKeysForItem(item, now))
    .sort()
    .join('|');
}

function pendingViewMetricKeysForItem(item, now) {
  const targets = Array.isArray(item.publishSummary?.targets) ? item.publishSummary.targets : [];
  return targets
    .filter(target => shouldPassiveRepaintTargetMetrics(target, now))
    .map(target => target.id || `${item.id}:${target.platform}:${target.publishedAt || ''}`);
}

function shouldPassiveRepaintTargetMetrics(target, now) {
  if (target.status !== 'published') return false;
  if (!ACTIVE_VIEW_METRIC_PLATFORMS.has(String(target.platform || '').toLowerCase())) return false;
  const publishedAt = timestamp(target.publishedAt);
  if (!publishedAt) return false;
  const ageMs = Math.max(0, now - publishedAt);
  if (ageMs >= 24 * 60 * 60 * 1000) return false;

  const metrics = target.metrics || target.analytics || target.insights || target.stats || null;
  return metrics?.viewsPending === true;
}

function activeMetricStaleMs(publishedAt) {
  const ageMs = Math.max(0, Date.now() - publishedAt);
  if (ageMs < 10 * 60 * 1000) return 60 * 1000;
  if (ageMs < 60 * 60 * 1000) return 5 * 60 * 1000;
  if (ageMs < 24 * 60 * 60 * 1000) return 15 * 60 * 1000;
  return 60 * 60 * 1000;
}

function activeBatchIdsForRows(rows) {
  return new Set(rows
    .map(row => row.contentItem || row)
    .filter(item => item?.generationBatchId && isActiveGeneratingItem(item))
    .map(item => item.generationBatchId));
}

function displayBatchIdsForRows(rows) {
  return new Set(rows
    .map(row => row.contentItem || row)
    .filter(item => item?.generationBatchId && shouldUseBatchForDisplay(item))
    .map(item => item.generationBatchId));
}

function shouldUseBatchForDisplay(item) {
  if (isActiveGeneratingItem(item)) return true;
  if (item.status === 'failed') return true;
  return item.generationStatus === 'failed';
}

function isActiveGeneratingItem(item) {
  if (!item) return false;
  if (item.status === 'generating') return true;
  return Boolean(item.generationStatus && !['ready', 'failed'].includes(item.generationStatus));
}

async function fetchBatchesForIds(batchIds) {
  if (!batchIds.size) return [];
  const results = await Promise.allSettled([...batchIds].map(id => api.content.getBatch(id)));
  return results
    .filter(result => result.status === 'fulfilled' && result.value?.batch)
    .map(result => batchWithJobs(result.value));
}

function batchDetailsNeeded(ids, batches, rows) {
  return new Set([...ids].filter(id => !batches.has(id) || (
    !Array.isArray(batches.get(id).jobs) && rows.some(row => {
      const item = row.contentItem || row;
      return item.generationBatchId === id && (item.status === 'failed' || item.generationStatus === 'failed');
    })
  )));
}

function mapContentRow(row, publishSummary = null, batch = null) {
  const item = row.contentItem || row;
  const version = row.activeVersion || null;
  const batchPlatforms = batch?.platforms || [];
  const platforms = preferredPlatforms(publishSummary, version);
  // Per-item job platforms, surfaced by content-worker so we don't have to
  // guess. For a language-split batch (e.g. IG-EN + TG-UK), each item's
  // jobPlatforms contains ONLY the platform(s) for its language group.
  // Without this we'd fall back to batch.platforms (the full IG+TG superset)
  // and render 4 tiles for a 2-item batch.
  const jobPlatforms = Array.isArray(row?.jobPlatforms) ? row.jobPlatforms : [];
  // Priority order: real publish_targets → per-item job platforms → batch superset
  const platformList = platforms.length
    ? platforms
    : (jobPlatforms.length ? jobPlatforms : batchPlatforms);
  const fallbackPlatform = item.format === 'image_post' ? 'instagram' : 'linkedin';
  const platform = platformList[0] || fallbackPlatform;
  const raw = enrichContentRow(row, batch, platformList.length ? platformList : [platform], publishSummary);
  const mediaPreview = contentMediaPreview(version, raw);
  const imageUrls = contentImageUrls(version, raw, mediaPreview);
  const generationSettled = ['ready', 'failed'].includes(item.generationStatus);
  // An AI image post whose TEXT is ready but whose IMAGE is still being generated
  // (visual job not yet terminal) is NOT truly ready — keep it 'generating' so the
  // dashboard never shows "ready" with no picture. Own/partial images and posts
  // with no visual job (null status) are settled and unaffected.
  const visualCooking = item.generationStatus !== 'failed' && item.status !== 'failed' && item.format === 'image_post'
    && (row?.jobComposeSource ?? 'ai') === 'ai'
    && ['queued', 'processing', 'generating', 'visualizing', 'pending'].includes(String(row?.jobVisualStatus || ''));
  const isGenerating = (!generationSettled && item.generationStatus && item.status === 'generating')
    || (visualCooking && !item.archivedAt && !publishSummaryStatus(publishSummary));
  const status = item.archivedAt
    ? 'archived'
    : isGenerating
      ? 'generating'
      : publishSummaryStatus(publishSummary) || generationStatusToContentStatus(item.generationStatus) || item.status || 'draft';
  return {
    id: item.id,
    raw,
    title: item.title || version?.hook || item.topic || 'Untitled content',
    type: formatType(item.format),
    platform,
    platforms: platformList.length ? platformList : [platform],
    status,
    generationBatchId: item.generationBatchId || batch?.id || null,
    generationStage: item.generationStatus || (status === 'generating' ? 'queued' : null),
    generationStatus: item.generationStatus || null,
    ...generationFailureForItem(row, batch),
    generationProgress: Number(item.generationProgress ?? (status === 'generating' ? 5 : 100)),
    // Surface backend's per-job visual_status so tileStatus / preview can
    // detect "text done, image still cooking" — keeps a composing overlay
    // visible even after item.status flips to 'scheduled' / 'published'
    // for the text part.
    visualStatus: row?.jobVisualStatus ?? null,
    visualCompletedAt: row?.jobVisualCompletedAt ?? null,
    jobFormat: row?.jobFormat ?? null,
    // Composition source ('own' | 'partial' | 'ai') so the batch progress rail
    // can avoid showing AI-generation steps for a fully user-supplied post.
    composeSource: row?.jobComposeSource ?? 'ai',
    date: formatDate(publishSummary?.lastPublishedAt || publishSummary?.lastFailedAt || publishSummary?.lastScheduledAt || item.updatedAt || item.createdAt),
    publishedAt: publishSummary?.lastPublishedAt || null,
    failedAt: publishSummary?.lastFailedAt || null,
    scheduledAt: publishSummary?.lastScheduledAt || item.publishScheduleAt || null,
    updatedAt: item.updatedAt,
    createdAt: item.createdAt,
    publishMeta: publishSummaryMeta(publishSummary),
    publishSummary,
    metrics: contentMetrics(raw, publishSummary),
    previewText: contentPreviewText(version, item),
    imageUrl: imageUrls[0] || mediaPreview.url,
    imageUrls,
    mediaPreview,
    batch,
  };
}

function generationStatusToContentStatus(status) {
  if (status === 'ready') return 'ready';
  if (status === 'failed') return 'failed';
  return null;
}

function enrichContentRow(row, batch, platforms, publishSummary) {
  return {
    ...row,
    generationBatch: batch || row.generationBatch || null,
    publishSummary: publishSummary || row.publishSummary || null,
    publishPlatforms: row.publishPlatforms || platforms,
    publishTargetAccountIds: row.publishTargetAccountIds || batch?.targetAccountIds || [],
    publishMode: row.publishMode || batch?.scheduleMode || 'draft',
    publishScheduleAt: row.publishScheduleAt || publishSummary?.lastScheduledAt || batch?.scheduleStartAt || null,
  };
}

function buildScheduleByContent(posts) {
  const map = new Map();
  posts.forEach(row => {
    const post = row.post || row;
    const contentId = post.contentItemId || row.content?.contentItem?.id;
    if (!contentId) return;
    const summary = map.get(contentId) || {
      platforms: [],
      publishedPlatforms: [],
      publishedCount: 0,
      failedCount: 0,
      activeCount: 0,
      lastPublishedAt: null,
      lastFailedAt: null,
      lastScheduledAt: null,
      scheduledPostId: null,
      postStatus: null,
      targets: [],
    };
    summary.scheduledPostId = summary.scheduledPostId || post.id;
    summary.postStatus = publishSummaryStatusFromPost(post.status, summary.postStatus);
    (row.targets || []).forEach(target => {
      if (!summary.platforms.includes(target.platform)) summary.platforms.push(target.platform);
      summary.targets.push(target);
      if (target.status === 'published') {
        summary.publishedCount += 1;
        if (!summary.publishedPlatforms.includes(target.platform)) summary.publishedPlatforms.push(target.platform);
      }
      if (['failed', 'needs_reconnect'].includes(target.status)) summary.failedCount += 1;
      if (['pending', 'queued', 'publishing'].includes(target.status) || ['planned', 'approved', 'queued'].includes(post.status)) summary.activeCount += 1;
      summary.lastPublishedAt = latestDate(summary.lastPublishedAt, target.publishedAt);
      if (['failed', 'needs_reconnect'].includes(target.status)) summary.lastFailedAt = latestDate(summary.lastFailedAt, target.failedAt || target.updatedAt);
      summary.lastScheduledAt = latestDate(summary.lastScheduledAt, target.scheduledAt || post.scheduledAt);
    });
    map.set(contentId, summary);
  });
  return map;
}

function publishSummaryStatus(summary) {
  if (!summary) return null;
  if (summary.failedCount > 0) return 'failed';
  if (summary.targets?.some(target => target.status === 'needs_manual_action')
    && summary.targets.every(target => ['published', 'needs_manual_action'].includes(target.status))) return 'inbox';
  if (['failed', 'partial_failure'].includes(summary.postStatus)) return 'failed';
  if (summary.postStatus === 'planned') return 'planned';
  if (publishSummaryIsPublishing(summary)) return 'publishing';
  if (summary.activeCount > 0) return 'scheduled';
  if (summary.targets?.length > 0) return summary.targets.every(target => target.status === 'published') ? 'published' : 'ready';
  if (summary.postStatus === 'published') return 'published';
  if (summary.postStatus === 'scheduled') return 'scheduled';
  return null;
}

function publishSummaryIsPublishing(summary) {
  const targets = Array.isArray(summary?.targets) ? summary.targets : [];
  return targets.some(target => ['queued', 'publishing'].includes(target.status))
    || targets.some(target => target.status === 'pending' && timestamp(target.scheduledAt) <= Date.now());
}

function preferredPlatforms(summary, version) {
  if (summary?.platforms?.length) return summary.platforms;
  if (summary?.publishedPlatforms?.length) return summary.publishedPlatforms;
  return Object.keys(version?.platformVersions || {}).filter(key => !key.startsWith('__'));
}

function contentMetrics(row, summary) {
  const targets = Array.isArray(summary?.targets) ? summary.targets : [];
  const publishedTargets = targets.filter(target => target.status === 'published');
  if (targets.length) return sumMetrics(publishedTargets);
  return firstMetrics([row?.metrics, row?.analytics, row?.insights]);
}

function sumMetrics(items) {
  return aggregateMetrics(items.map(item => normalizeMetrics(item.metrics || item.analytics || item.insights || item.stats, item.metricFetch)));
}

function firstMetrics(sources) {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    return normalizeMetrics(source);
  }
  return normalizeMetrics(null);
}

function publishSummaryStatusFromPost(next, current) {
  if (current === 'failed' || ['failed', 'partial_failure'].includes(next)) return 'failed';
  if (current === 'scheduled' || ['approved', 'queued', 'scheduled'].includes(next)) return 'scheduled';
  if (current === 'published' || next === 'published') return 'published';
  if (current === 'planned' || next === 'planned') return 'planned';
  return current || next || null;
}

function publishSummaryMeta(summary) {
  if (!summary) return '';
  const platformText = summary.platforms.map(platformLabel).join(', ');
  if (summary.failedCount > 0 && summary.activeCount === 0) return `Failed on ${platformText}`;
  if (summary.postStatus === 'planned') return `Needs approval for ${platformText}`;
  if (publishSummaryIsPublishing(summary)) return `Publishing to ${platformText}`;
  if (summary.activeCount > 0) return `Scheduled for ${platformText}`;
  if (summary.targets?.length > 0 && summary.targets.every(target => target.status === 'published')) return `Published to ${platformText}`;
  if (!summary.targets?.length && summary.postStatus === 'published') return `Published to ${platformText}`;
  return platformText;
}

function contentPreviewText(version, item) {
  const platformVersions = version?.platformVersions || {};
  const platformText = Object.values(platformVersions).find(value => typeof value === 'string' && value.trim());
  const value = version?.caption || version?.body || version?.hook || platformText || item.topic || item.title || '';
  return String(value).replace(/\s+/g, ' ').trim() || 'Content from your workspace';
}

function contentMediaPreview(version, row) {
  const preview = row?.mediaPreview || version?.mediaPreview || row?.media_preview || version?.media_preview || null;
  const url = stringUrl(preview?.url || preview?.thumbnailUrl || preview?.imageUrl || preview?.src);
  const count = Number(preview?.count ?? preview?.mediaCount ?? row?.mediaCount ?? row?.media_count ?? 0);
  return {
    url,
    thumbnailUrl: stringUrl(preview?.thumbnailUrl) || url,
    count: Number.isFinite(count) && count > 0 ? count : (url ? 1 : 0),
    hasCarousel: Boolean(preview?.hasCarousel) || count > 1,
  };
}

function contentImageUrls(version, row, mediaPreview = null) {
  const seen = new Set();
  const out = [];
  const push = value => {
    if (typeof value !== 'string' || !/^https?:\/\//.test(value) || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  };
  push(mediaPreview?.url);
  push(mediaPreview?.thumbnailUrl);
  (row?.mediaUrls || row?.media_urls || []).forEach(push);
  (version?.mediaUrls || version?.media_urls || []).forEach(push);
  push(version?.imageUrl);
  push(version?.mediaUrl);
  push(version?.thumbnailUrl);
  push(version?.assetUrl);
  (row?.carouselSlides || row?.slides || []).forEach(slide => {
    push(slide.imageUrl);
    push(slide.publicUrl);
    push(slide.public_url);
    push(slide.mediaAsset?.publicUrl);
    push(slide.mediaAsset?.public_url);
  });
  Object.values(version?.platformVersions || {}).forEach(value => {
    if (value && typeof value === 'object') {
      push(value.imageUrl);
      push(value.mediaUrl);
      push(value.thumbnailUrl);
      push(value.assetUrl);
    }
  });
  return out;
}

function stringUrl(value) {
  return typeof value === 'string' && /^https?:\/\//.test(value) ? value : null;
}

function carouselPlaceholderUrl(index) {
  const label = encodeURIComponent(`Dathent carousel slide ${index}`);
  return `https://placehold.co/1080x1350/111827/FFFFFF/png?text=${label}`;
}

function latestDate(current, next) {
  if (!next) return current;
  if (!current) return next;
  return Date.parse(next) > Date.parse(current) ? next : current;
}

function platformLabel(platform) {
  const labels = { instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook' };
  return labels[platform] || platform;
}

function formatType(format) {
  const labels = { text_post: 'Text post', image_post: 'Image', carousel: 'Carousel', video: 'Video', story: 'Story' };
  return labels[format] || 'Content';
}

function formatDate(value) {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return formatAppDate(date, { month: 'short', day: 'numeric' });
}
